// Client portal — Account Management page.
//
// Three self-service surfaces for a signed-in client user:
//   1. Change password           — Supabase auth.updateUser({ password })
//                                   We re-verify the current password by
//                                   doing a signInWithPassword check first,
//                                   so a thief with a hijacked session can't
//                                   just rotate the password.
//   2. Change email              — Supabase auth.updateUser({ email })
//                                   Triggers Supabase's confirmation flow:
//                                   the user clicks a link sent to BOTH the
//                                   old and new addresses before the change
//                                   takes effect.
//   3. Team members              — list / invite / remove via /me/clients/{id}/team
//
// If the user is linked to multiple clients we surface a selector at the top
// of the Team section (same pattern as the main Account page).

import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail, KeyRound, Users, Loader2, UserPlus, X, AlertTriangle, CheckCircle2, Trash2,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { ToastContainer, useToast } from '../../components/Toast'
import { getMe, getMeTeam, inviteTeamMember, removeTeamMember } from '../../lib/api'

export default function AccountSettings() {
  const { user } = useAuth()
  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()

  // Which client's team to show. If user has just one client this is fixed.
  const { data: me, error: meError } = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    staleTime: 60_000,
  })
  const clients = me?.clients || []
  const [activeClientId, setActiveClientId] = useState(null)
  const effectiveClientId = activeClientId || clients[0]?.id || null

  return (
    <div className="max-w-3xl space-y-6 sm:p-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Account Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Manage your sign-in details and the teammates who can access your account.
        </p>
      </div>

      <ChangePasswordCard
        currentEmail={user?.email}
        onSuccess={() => addToast({ type: 'success', message: 'Password updated.' })}
        onError={(msg) => addToast({ type: 'error', message: msg })}
      />

      <ChangeEmailCard
        currentEmail={user?.email}
        onSuccess={() => addToast({
          type: 'success',
          message: 'Confirmation email sent. Click the link to finalise the change.',
          duration: 8000,
        })}
        onError={(msg) => addToast({ type: 'error', message: msg })}
      />

      {/* `me?.clients || []` collapses a failed fetch into "no clients", which
          TeamCard renders as "your account isn't linked to a client yet" — a
          false claim that sends the user to their account manager. */}
      {meError ? (
        <SectionCard icon={Users} title="Team members">
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Couldn&rsquo;t load your account</p>
              <p className="text-xs mt-0.5">
                This does <b>not</b> mean your account is unlinked — the request failed.
              </p>
              <p className="text-xs font-mono mt-1 break-all">
                {meError?.response?.data?.detail || meError?.message || 'Unknown error'}
              </p>
            </div>
          </div>
        </SectionCard>
      ) : (
        <TeamCard
          clientId={effectiveClientId}
          clients={clients}
          activeClientId={activeClientId}
          onClientChange={setActiveClientId}
          onSuccess={(msg) => addToast({ type: 'success', message: msg })}
          onError={(msg) => addToast({ type: 'error', message: msg })}
          invalidateTeam={() => queryClient.invalidateQueries({ queryKey: ['me-team'] })}
        />
      )}
    </div>
  )
}


// ─── Change password ────────────────────────────────────────────────────────


function ChangePasswordCard({ currentEmail, onSuccess, onError }) {
  const [current, setCurrent] = useState('')
  const [next1, setNext1]     = useState('')
  const [next2, setNext2]     = useState('')
  const [busy, setBusy]       = useState(false)
  const [err, setErr]         = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (next1.length < 8) return setErr('New password must be at least 8 characters.')
    if (next1 !== next2)  return setErr("New password and confirmation don't match.")
    if (!currentEmail)    return setErr('No email on session — try signing out and back in.')

    setBusy(true)
    try {
      // 1) Re-verify the current password by signing in again. If this
      //    succeeds, we know the person at the keyboard is really them.
      const { error: signinErr } = await supabase.auth.signInWithPassword({
        email: currentEmail, password: current,
      })
      if (signinErr) {
        setBusy(false)
        return setErr('Current password is incorrect.')
      }
      // 2) Apply the new password.
      const { error: updateErr } = await supabase.auth.updateUser({ password: next1 })
      if (updateErr) {
        setBusy(false)
        return setErr(updateErr.message || 'Password update failed.')
      }
      setCurrent(''); setNext1(''); setNext2('')
      onSuccess()
    } catch (e) {
      onError(e.message || 'Password update failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard icon={KeyRound} title="Change password">
      <form onSubmit={handleSubmit} className="space-y-3">
        <PasswordField
          label="Current password"
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
          required
        />
        <PasswordField
          label="New password"
          value={next1}
          onChange={setNext1}
          autoComplete="new-password"
          required
          hint="At least 8 characters."
        />
        <PasswordField
          label="Confirm new password"
          value={next2}
          onChange={setNext2}
          autoComplete="new-password"
          required
        />
        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{err}</span>
          </div>
        )}
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Update password
          </button>
        </div>
      </form>
    </SectionCard>
  )
}


// ─── Change email ───────────────────────────────────────────────────────────


function ChangeEmailCard({ currentEmail, onSuccess, onError }) {
  const [newEmail, setNewEmail] = useState('')
  const [busy, setBusy]         = useState(false)
  const [err, setErr]           = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    const candidate = newEmail.trim().toLowerCase()
    if (!candidate)                           return setErr('Enter a new email address.')
    if (!/^\S+@\S+\.\S+$/.test(candidate))    return setErr('That email looks invalid.')
    if (candidate === (currentEmail || '').toLowerCase()) {
      return setErr('That is already your current email.')
    }

    setBusy(true)
    try {
      const { error } = await supabase.auth.updateUser({ email: candidate })
      if (error) {
        setErr(error.message || 'Email update failed.')
      } else {
        setNewEmail('')
        onSuccess()
      }
    } catch (e) {
      onError(e.message || 'Email update failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard icon={Mail} title="Change email">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Current email</label>
          <input
            type="email"
            value={currentEmail || ''}
            disabled
            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded bg-slate-50 text-slate-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">New email</label>
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="you@newdomain.com"
            required
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-slate-400 mt-1">
            You'll get a confirmation link at both addresses. The change takes effect once you click it.
          </p>
        </div>
        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{err}</span>
          </div>
        )}
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Send confirmation
          </button>
        </div>
      </form>
    </SectionCard>
  )
}


// ─── Team ───────────────────────────────────────────────────────────────────


function TeamCard({ clientId, clients, activeClientId, onClientChange, onSuccess, onError, invalidateTeam }) {
  const queryClient = useQueryClient()
  const [showInvite, setShowInvite] = useState(false)
  const [pendingRemoveId, setPendingRemoveId] = useState(null)

  const { data: team = [], isLoading, error } = useQuery({
    queryKey: ['me-team', clientId],
    queryFn:  () => getMeTeam(clientId),
    enabled:  !!clientId,
  })

  const inviteMut = useMutation({
    mutationFn: (payload) => inviteTeamMember(clientId, payload),
    onSuccess: (member) => {
      // An address that already had an account is linked in place and gets
      // no email — re-inviting it would reset their email confirmation and
      // lock them out. Say which actually happened rather than always
      // claiming an invite went out.
      onSuccess(
        member?.invite_email_sent === false
          ? 'That account already existed — added to your team. No email sent.'
          : 'Invite sent.'
      )
      invalidateTeam()
      setShowInvite(false)
    },
    onError: (err) => onError(err.response?.data?.detail || 'Invite failed.'),
  })

  const removeMut = useMutation({
    mutationFn: (memberId) => removeTeamMember(clientId, memberId),
    onSuccess: () => {
      onSuccess('Teammate removed.')
      invalidateTeam()
      setPendingRemoveId(null)
    },
    onError: (err) => onError(err.response?.data?.detail || 'Remove failed.'),
  })

  if (clients.length === 0) {
    return (
      <SectionCard icon={Users} title="Team">
        <p className="text-sm text-slate-500">
          Your account isn't linked to a client yet — contact your account manager.
        </p>
      </SectionCard>
    )
  }

  return (
    <SectionCard
      icon={Users}
      title="Team members"
      headerRight={
        clients.length > 1 && (
          <select
            value={activeClientId || clients[0]?.id || ''}
            onChange={e => onClientChange(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )
      }
    >
      <p className="text-xs text-slate-500 mb-3">
        These users can sign in and see everything on this account. Invited
        teammates set their own password when they accept the email link.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : error ? (
        // The `= []` default would show "No teammates yet." on a failed fetch,
        // i.e. assert that nobody else can sign in to this account.
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Couldn&rsquo;t load your team</p>
            <p className="text-xs mt-0.5">
              This is <b>not</b> an empty team list — nobody has been removed.
            </p>
            <p className="text-xs font-mono mt-1 break-all">
              {error?.response?.data?.detail || error?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      ) : team.length === 0 ? (
        <p className="text-sm text-slate-400">No teammates yet.</p>
      ) : (
        <ul className="border border-slate-200 rounded divide-y divide-slate-100">
          {team.map(m => (
            <li key={m.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-800 font-medium">
                    {m.full_name || m.email || 'Unnamed user'}
                  </span>
                  {m.is_you && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You</span>
                  )}
                  {m.client_role === 'client_admin' && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">Admin</span>
                  )}
                  {m.status === 'invited' && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Invited</span>
                  )}
                  {m.status === 'disabled' && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-700">Disabled</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </div>
              {!m.is_you && (
                <button
                  onClick={() => setPendingRemoveId(m.id)}
                  className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                  title="Remove from team"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex justify-end">
        <button
          onClick={() => setShowInvite(true)}
          className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1.5"
        >
          <UserPlus size={14} /> Invite teammate
        </button>
      </div>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onSubmit={(payload) => inviteMut.mutate(payload)}
          isPending={inviteMut.isPending}
        />
      )}
      {pendingRemoveId && (
        <RemoveModal
          member={team.find(m => m.id === pendingRemoveId)}
          onCancel={() => setPendingRemoveId(null)}
          onConfirm={() => removeMut.mutate(pendingRemoveId)}
          isPending={removeMut.isPending}
        />
      )}
    </SectionCard>
  )
}


function InviteModal({ onClose, onSubmit, isPending }) {
  const [email, setEmail]       = useState('')
  const [fullName, setFullName] = useState('')
  const [err, setErr]           = useState(null)

  function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!email.trim())                       return setErr('Email is required.')
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setErr('That email looks invalid.')
    if (!fullName.trim())                    return setErr('Full name is required.')
    onSubmit({ email: email.trim(), full_name: fullName.trim() })
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <h3 className="text-base font-semibold text-slate-800">Invite a teammate</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
      </div>
      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            required
            autoFocus
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="Charlie Middleton"
            required
            className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <p className="text-xs text-slate-500">
          They'll get an invite email to set their own password. The account
          will have the same access you do.
        </p>
        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
            {err}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            Send invite
          </button>
        </div>
      </form>
    </ModalShell>
  )
}


function RemoveModal({ member, onCancel, onConfirm, isPending }) {
  if (!member) return null
  return (
    <ModalShell onClose={onCancel}>
      <div className="px-6 py-5">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 size={18} className="text-red-500" />
          <h3 className="text-base font-semibold text-slate-800">Remove teammate</h3>
        </div>
        <p className="text-sm text-slate-600">
          Remove <b>{member.full_name || member.email}</b> from this account?
          They'll lose access immediately, but their sign-in is preserved in case
          you want to re-add them later.
        </p>
        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {isPending && <Loader2 size={12} className="animate-spin" />}
            Remove
          </button>
        </div>
      </div>
    </ModalShell>
  )
}


// ─── Shared primitives ──────────────────────────────────────────────────────


function SectionCard({ icon: Icon, title, children, headerRight }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          {title}
        </h2>
        {headerRight}
      </div>
      {children}
    </div>
  )
}


function PasswordField({ label, value, onChange, autoComplete, required, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="password"
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  )
}


function ModalShell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          onClick={e => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
